import { X } from "@phosphor-icons/react"
import type { HTMLAttributes, ReactNode } from "react"
import {
  Heading as AriaHeading,
  type HeadingProps,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { IconButton, type IconButtonProps } from "../../button/IconButton"
import { Icon } from "../../icon"
import type { IconProps } from "../../icon/Icon"

export const ModalCloseButton = ({
  className,
  ...props
}: Omit<IconButtonProps, "icon" | "label"> & {
  icon?: IconButtonProps["icon"]
}) => (
  <IconButton
    variant="ghost"
    label="Close"
    slot="close"
    size="small"
    icon={X}
    className={twMerge("flex-shrink-0", className)}
    {...props}
  />
)

export const HeaderTitle = ({
  className,
  ...props
}: HeadingProps & { className?: string }) => (
  <AriaHeading
    slot="title"
    level={2}
    className={twMerge(
      "tt-modal-heading font-medium tracking-tight leading-none",
      className
    )}
    {...props}
  />
)

export const HeaderSubtitle = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge("tt-modal-subheading text-sm leading-none", className)}
    {...props}
  />
)

export const HeaderIcon = ({ as, ...props }: IconProps) => (
  <div className="flex flex-col items-center justify-center h-[20px]">
    <Icon
      as={as}
      width="16px"
      style={{ opacity: 0.5 }}
      aria-hidden
      {...props}
    />
  </div>
)

export const HeaderTitleStack = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("flex flex-col gap-1", className)} {...props} />
)

export const HeaderTitleIconRow = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge(
      "flex items-center justify-start gap-3 flex-1",
      className
    )}
    {...props}
  />
)

export const HeaderContainer = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge(
      "flex items-center justify-start tt-modal-header",
      className
    )}
    {...props}
  />
)

export const Header = ({
  subtitle,
  title,
  icon,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  subtitle?: ReactNode
  title: ReactNode
  icon?: IconProps["as"]
}) => {
  return (
    <HeaderContainer {...props}>
      <HeaderTitleIconRow>
        {icon && <HeaderIcon as={icon} />}
        <HeaderTitleStack>
          <HeaderTitle>{title}</HeaderTitle>
          {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
        </HeaderTitleStack>
      </HeaderTitleIconRow>
      <ModalCloseButton className="-m-1" />
    </HeaderContainer>
  )
}
